/*
  Hero slideshow.

  Full-bleed media only. Crossfade between slides, autoplay that yields to the
  visitor, and video playback scoped to the active slide.

  The header offset is measured rather than hardcoded. The header group holds
  an announcement bar, a wrapping promo marquee and the header itself, so its
  height differs between breakpoints and changes again when the promo bar wraps.
  A fixed value would be wrong at most widths; a ResizeObserver keeps the CSS
  custom property honest.
*/
class HeroSlideshow extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('.hero-slideshow__slide'));
    if (this.slides.length === 0) return;

    this.dots = Array.from(this.querySelectorAll('.hero-slideshow__dot'));
    this.index = 0;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.measureHeader();
    this.bindControls();
    this.bindAutoplayPauses();

    this.embedHolders = Array.from(this.querySelectorAll('[data-embed-url]'));
    this.bindEmbedPlayButtons();
    this.watchViewport();

    this.reducedMotion.addEventListener('change', () => this.applyMotionMode());
    this.applyMotionMode();
    this.syncVideos();
    this.syncEmbeds();
  }

  disconnectedCallback() {
    this.stop();
    if (this.headerObserver) this.headerObserver.disconnect();
    if (this.viewportObserver) this.viewportObserver.disconnect();
    if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
  }

  /* Viewport ------------------------------------------------------------ */

  /*
    syncEmbeds ties the iframe to the active slide, which says nothing about
    whether the section is on screen. With autoplay running, a hero scrolled
    far out of view still cycled round to its video slide and mounted a
    YouTube player nobody could see. This observer supplies the other half of
    that condition.

    Mounting is gated on it rather than only tearing down on exit, so a slide
    becoming active off screen never mounts in the first place. Coming back
    into view re-runs syncEmbeds, which mounts the active slide's embed if it
    has one, so a visitor scrolling back finds the hero playing rather than
    holding a poster.

    inViewport starts true. If IntersectionObserver is missing, or before the
    first callback arrives, the behaviour is what it was before this observer
    existed rather than a hero that never mounts anything.
  */
  watchViewport() {
    this.inViewport = true;
    if (!('IntersectionObserver' in window)) return;

    this.viewportObserver = new IntersectionObserver(
      (entries) => {
        this.inViewport = entries[0].isIntersecting;
        if (this.inViewport) this.syncEmbeds();
        else this.embedHolders.forEach((holder) => this.unmountEmbed(holder));
      },
      { threshold: 0 }
    );

    this.viewportObserver.observe(this);
  }

  /* Header offset ------------------------------------------------------- */

  /*
    True when this hero is the page's first section, which is the condition
    section-header-transparent.css gates its whole stylesheet on. Read once per
    measure rather than cached, because the theme editor can reorder sections
    without reloading the page.
  */
  get overlaidByHeader() {
    const section = this.closest('.shopify-section');
    if (!section || !section.parentElement) return false;
    return section.parentElement.firstElementChild === section;
  }

  measureHeader() {
    const header = document.querySelector('.section-header, #shopify-section-header, header');
    const group = header ? header.closest('.shopify-section-group-header-group') || header : null;
    const apply = () => {
      const nodes = document.querySelectorAll('.shopify-section-group-header-group');
      let total = 0;
      let overlay = 0;
      if (nodes.length > 0) {
        nodes.forEach((node) => {
          const height = node.getBoundingClientRect().height;

          /*
            A transparent header overlays this section rather than displacing
            it, so it contributes nothing to the offset and the hero runs the
            full height of the viewport beneath the promo bar. The announcement
            bar and the marquee are still in flow and are still counted.

            The same two conditions as section-header-transparent.css: the
            setting is on, and this hero is the page's first section. Getting
            those out of step would leave a header's worth of gap under the
            hero, or a header's worth of it past the fold.

            Keyed off the class rather than off computed position on purpose.
            Position is not a stable signal here: Dawn's sticky state turns any
            header, transparent or not, into an out-of-flow box while scrolled,
            and a re-measure at that moment would resize the hero mid-scroll.
          */
          if (this.overlaidByHeader && node.querySelector('.header-wrapper--transparent')) {
            overlay = height;
            return;
          }

          total += height;
        });
      } else if (group) {
        total = group.getBoundingClientRect().height;
      }
      this.style.setProperty('--hero-header-offset', `${Math.round(total)}px`);

      /*
        On the root rather than on this element, because the stylesheet applies
        it to the section wrapper above and custom properties inherit down, not
        up. Zero whenever the header is not overlaying, so the negative margin
        resolves to nothing and the hero sits below the header as before.
      */
      document.documentElement.style.setProperty('--hero-header-overlay', `${Math.round(overlay)}px`);
    };

    apply();

    /*
      Measuring once at connectedCallback is not enough and gets a badly wrong
      answer. At that point the promo bar's own stylesheet has not necessarily
      applied yet, so its list is still a stacked column rather than a row and
      the header group measures roughly three times its settled height. That
      produced a 517px offset against a real 166px, and a hero half the height
      it should be.

      So re-measure at every point the header box can still change: the next
      frame, window load once stylesheets and images are in, and font swap,
      which shifts text heights. ResizeObserver covers everything after that,
      but it is rAF-driven and will not fire in a background tab, so it cannot
      be the only safeguard.
    */
    requestAnimationFrame(apply);
    window.addEventListener('load', apply);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(apply).catch(() => {});
    }

    if ('ResizeObserver' in window) {
      this.headerObserver = new ResizeObserver(apply);
      document.querySelectorAll('.shopify-section-group-header-group').forEach((node) => {
        this.headerObserver.observe(node);
      });
    }
    window.addEventListener('resize', apply);
  }

  /* Motion --------------------------------------------------------------- */

  applyMotionMode() {
    if (this.reducedMotion.matches) {
      // No autoplay and no crossfade, but arrows, dots and keys keep working.
      this.classList.add('hero-slideshow--no-motion');
      this.stop();
      // Tear down anything already mounted: the setting can change mid-visit.
      this.querySelectorAll('.hero-slideshow__embed').forEach((frame) => frame.remove());
      return;
    }
    this.classList.remove('hero-slideshow--no-motion');
    this.start();
    // The setting can be turned back off mid-visit, so remount what is active.
    this.syncEmbeds();
  }

  /* Autoplay ------------------------------------------------------------- */

  get autoplayEnabled() {
    return this.dataset.autoplay === 'true' && this.slides.length > 1;
  }

  start() {
    this.stop();
    if (!this.autoplayEnabled) return;
    if (this.reducedMotion.matches) return;
    const seconds = parseInt(this.dataset.autoplayInterval, 10) || 6;
    this.timer = setInterval(() => this.select(this.index + 1, { focus: false }), seconds * 1000);
  }

  stop() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  bindAutoplayPauses() {
    this.addEventListener('mouseenter', () => this.stop());
    this.addEventListener('mouseleave', () => this.start());
    this.addEventListener('focusin', () => this.stop());
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.start();
    });

    // A background tab should not burn autoplay ticks or video decode.
    this.onVisibility = () => {
      if (document.hidden) {
        this.stop();
        this.slides.forEach((slide) => {
          const video = slide.querySelector('video');
          if (video) video.pause();
        });
      } else {
        this.start();
        this.syncVideos();
      }
    };
    document.addEventListener('visibilitychange', this.onVisibility);
  }

  /* Controls ------------------------------------------------------------- */

  bindControls() {
    const prev = this.querySelector('button[name="previous"]');
    const next = this.querySelector('button[name="next"]');
    if (prev) prev.addEventListener('click', () => this.select(this.index - 1));
    if (next) next.addEventListener('click', () => this.select(this.index + 1));

    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.select(i)));

    this.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      this.select(event.key === 'ArrowRight' ? this.index + 1 : this.index - 1);
    });
  }

  /* Slide state ---------------------------------------------------------- */

  select(next, options) {
    const count = this.slides.length;
    const target = ((next % count) + count) % count;
    this.index = target;

    this.slides.forEach((slide, i) => {
      const active = i === target;
      slide.classList.toggle('hero-slideshow__slide--active', active);
      if (active) {
        slide.removeAttribute('aria-hidden');
      } else {
        slide.setAttribute('aria-hidden', 'true');
      }
    });

    this.dots.forEach((dot, i) => {
      const active = i === target;
      dot.classList.toggle('hero-slideshow__dot--active', active);
      dot.setAttribute('aria-current', active ? 'true' : 'false');
    });

    this.syncVideos();
    this.syncEmbeds();
    if (options && options.focus === false) return;
  }

  /* External video ------------------------------------------------------- */

  /*
    An iframe cannot be paused from outside the frame. Without the YouTube or
    Vimeo JS API there is no pause() to call, and an off-screen iframe keeps
    downloading and playing regardless of what the slide looks like.

    So the iframe is not markup at all: it exists only while its slide is the
    active one, and is removed the moment the slide is deactivated. That makes
    "inactive" mean genuinely not loading rather than merely not visible, and it
    also means a hero with several video slides loads one embed rather than all
    of them on first paint.
  */
  syncEmbeds() {
    if (!this.embedHolders || this.embedHolders.length === 0) return;

    this.embedHolders.forEach((holder) => {
      const slide = holder.closest('.hero-slideshow__slide');
      const isActive = slide === this.slides[this.index];

      if (!isActive) {
        this.unmountEmbed(holder);
        return;
      }

      /*
        Reduced motion never auto-loads. The poster stays and the play button
        in the markup becomes the way in, so the visitor decides rather than
        the theme.
      */
      if (this.reducedMotion.matches) return;

      // Off screen is not "inactive", but it is just as good a reason not to load.
      if (!this.inViewport) return;

      this.mountEmbed(holder);
    });
  }

  mountEmbed(holder) {
    if (holder.querySelector('iframe')) return;

    /*
      One player on the page, not one per section. The registry tears down
      whoever held it before - another hero slide, or a card in the editorial
      carousel - so a hero slide becoming active can no longer leave an unmuted
      carousel video running underneath it. Claimed before the iframe exists so
      the two are never mounted at the same moment.
    */
    window.themeVideoRegistry?.claim(holder, () => this.unmountEmbed(holder));

    const iframe = document.createElement('iframe');
    iframe.className = 'hero-slideshow__embed';
    iframe.src = holder.dataset.embedUrl;
    iframe.title = holder.dataset.embedTitle || '';
    iframe.allow = 'autoplay; encrypted-media; picture-in-picture';
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute('loading', 'lazy');
    holder.appendChild(iframe);
  }

  /*
    The only place an embed is torn down, so the registry hears about every
    removal: slide deactivation, and the viewport observer added alongside it.
  */
  unmountEmbed(holder) {
    const iframe = holder.querySelector('iframe');
    if (!iframe) return;
    iframe.remove();
    window.themeVideoRegistry?.release(holder);
  }

  bindEmbedPlayButtons() {
    this.querySelectorAll('[data-embed-play]').forEach((button) => {
      button.addEventListener('click', () => {
        const holder = button.closest('[data-embed-url]');
        if (holder) this.mountEmbed(holder);
      });
    });
  }

  syncVideos() {
    this.slides.forEach((slide, i) => {
      const video = slide.querySelector('video');
      if (!video) return;
      if (i === this.index && !document.hidden) {
        const played = video.play();
        if (played) played.catch(() => {});
      } else {
        video.pause();
      }
    });
  }
}

customElements.define('hero-slideshow', HeroSlideshow);
